import React from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicyPage() {
    const lastUpdated = "March 16, 2026";

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 sm:p-12">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                    Privacy Policy
                </h1>
                <p className="text-gray-500 mb-8">Last updated: {lastUpdated}</p>

                <div className="space-y-8 text-gray-700 leading-relaxed">
                    {/* Introduction */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Introduction</h2>
                        <p>
                            OmniWorth LLC ("OmniWorth", "we", "us") is committed to protecting your privacy. This Privacy Policy
                            explains how we collect, use, and safeguard your information when you
                            use our net worth tracking application.
                        </p>
                        <p className="mt-3">
                            By using OmniWorth, you agree to the collection and use of information
                            in accordance with this policy.
                        </p>
                    </section>

                    {/* Information We Collect */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Information We Collect</h2>

                        <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">Account Information</h3>
                        <p>When you create an account, we collect:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                            <li>Name</li>
                            <li>Email address</li>
                            <li>Password (stored securely using bcrypt hashing)</li>
                        </ul>

                        <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">Financial Data</h3>
                        <p>
                            When you connect your financial accounts through Plaid, we receive and store:
                        </p>
                        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                            <li>Account names, types, and balances</li>
                            <li>Transaction history</li>
                            <li>Investment holdings and positions</li>
                            <li>Institution names</li>
                        </ul>
                        <p className="mt-3 text-sm bg-gray-50 border border-gray-200 p-3 rounded-lg">
                            <strong className="text-gray-900">Important:</strong> We never receive or store
                            your bank login credentials. All authentication is handled securely by Plaid.
                        </p>

                        <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">Usage Data</h3>
                        <p>We automatically collect certain information when you use the Service:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                            <li>IP address and device information</li>
                            <li>Browser type and version</li>
                            <li>Pages visited and features used</li>
                            <li>Time and date of access</li>
                        </ul>
                    </section>

                    {/* How We Use Your Information */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">How We Use Your Information</h2>
                        <p>We use the information we collect to:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                            <li>Provide, maintain, and improve the Service</li>
                            <li>Calculate and display your net worth and financial data</li>
                            <li>Process your subscription and payments</li>
                            <li>Send you service-related communications</li>
                            <li>Respond to your inquiries and support requests</li>
                            <li>Detect and prevent fraud or unauthorized access</li>
                            <li>Comply with legal obligations</li>
                        </ul>
                    </section>

                    {/* Third-Party Services */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Third-Party Services</h2>
                        <p>We use the following third-party services to operate OmniWorth:</p>

                        <div className="mt-4 space-y-4">
                            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                                <h4 className="font-medium text-gray-900">Plaid</h4>
                                <p className="text-sm text-gray-500 mt-1">
                                    Plaid connects your bank accounts to OmniWorth. They handle all
                                    authentication with your financial institutions. View{" "}
                                    <a href="https://plaid.com/legal/" target="_blank" rel="noopener noreferrer"
                                       className="text-accent-600 hover:underline">
                                        Plaid's Privacy Policy
                                    </a>.
                                </p>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                                <h4 className="font-medium text-gray-900">Stripe</h4>
                                <p className="text-sm text-gray-500 mt-1">
                                    Stripe processes subscription payments. We never store your full
                                    credit card number. View{" "}
                                    <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer"
                                       className="text-accent-600 hover:underline">
                                        Stripe's Privacy Policy
                                    </a>.
                                </p>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                                <h4 className="font-medium text-gray-900">Amazon Web Services (AWS)</h4>
                                <p className="text-sm text-gray-500 mt-1">
                                    Our infrastructure is hosted on AWS in the United States. View{" "}
                                    <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer"
                                       className="text-accent-600 hover:underline">
                                        AWS Privacy Policy
                                    </a>.
                                </p>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                                <h4 className="font-medium text-gray-900">Sentry</h4>
                                <p className="text-sm text-gray-500 mt-1">
                                    Sentry provides error monitoring and crash reporting to help us identify
                                    and fix issues quickly. It may collect technical data such as your IP
                                    address, browser type, and error context. View{" "}
                                    <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer"
                                       className="text-accent-600 hover:underline">
                                        Sentry's Privacy Policy
                                    </a>.
                                </p>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                                <h4 className="font-medium text-gray-900">SendGrid</h4>
                                <p className="text-sm text-gray-500 mt-1">
                                    SendGrid delivers our transactional emails such as account verification
                                    and password reset emails. Your email address is shared with SendGrid
                                    for this purpose. View{" "}
                                    <a href="https://www.twilio.com/en-us/legal/privacy" target="_blank" rel="noopener noreferrer"
                                       className="text-accent-600 hover:underline">
                                        SendGrid's Privacy Policy
                                    </a>.
                                </p>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <h4 className="font-medium text-gray-900">Google Analytics</h4>
                                <p className="text-sm text-gray-500 mt-1">
                                    We use Google Analytics to understand how visitors use our website. It
                                    collects anonymous usage data such as pages visited, time spent, and
                                    referral sources. No personal financial data is shared with Google. View{" "}
                                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"
                                       className="text-accent-600 hover:underline">
                                        Google's Privacy Policy
                                    </a>.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Data Security */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Data Security</h2>
                        <p>We implement industry-standard security measures to protect your data:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                            <li>All data is encrypted in transit using TLS/HTTPS</li>
                            <li>Sensitive data is encrypted at rest using AES-256</li>
                            <li>Passwords are hashed using bcrypt (never stored in plain text)</li>
                            <li>Access to production systems is restricted and logged</li>
                            <li>Regular security updates and monitoring</li>
                        </ul>
                        <p className="mt-3">
                            While we strive to protect your information, no method of transmission
                            or storage is 100% secure. We cannot guarantee absolute security.
                        </p>
                    </section>

                    {/* Data Retention */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Data Retention</h2>
                        <p>
                            We retain your data for as long as your account is active or as needed
                            to provide you the Service. If you delete your account, we will delete
                            your personal data within 30 days, except where we are required to
                            retain it for legal or regulatory purposes.
                        </p>
                    </section>

                    {/* Your Rights */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Your Rights</h2>
                        <p>You have the right to:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                            <li><strong className="text-gray-900">Access</strong> - Request a copy of your personal data</li>
                            <li><strong className="text-gray-900">Correction</strong> - Request correction of inaccurate data</li>
                            <li><strong className="text-gray-900">Deletion</strong> - Request deletion of your account and data</li>
                            <li><strong className="text-gray-900">Disconnect</strong> - Remove linked financial accounts at any time</li>
                            <li><strong className="text-gray-900">Export</strong> - Export your data in a portable format</li>
                        </ul>
                        <p className="mt-3">
                            To exercise these rights, contact us at{" "}
                            <a href="mailto:support@omniworth.net" className="text-accent-600 hover:underline">
                                support@omniworth.net
                            </a>.
                        </p>
                    </section>

                    {/* Children's Privacy */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Children's Privacy</h2>
                        <p>
                            OmniWorth is not intended for users under 18 years of age. We do not
                            knowingly collect personal information from children. If you believe
                            we have collected information from a child, please contact us immediately.
                        </p>
                    </section>

                    {/* Changes to This Policy */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Changes to This Policy</h2>
                        <p>
                            We may update this Privacy Policy from time to time. We will notify you
                            of any material changes by posting the new policy on this page and updating
                            the "Last updated" date. Your continued use of the Service after changes
                            constitutes acceptance of the updated policy.
                        </p>
                    </section>

                    {/* Contact Us */}
                    <section>
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">Contact Us</h2>
                        <p>
                            If you have questions about this Privacy Policy or our data practices,
                            please contact us:
                        </p>
                        <ul className="mt-2 space-y-1 text-gray-500">
                            <li>Email: <a href="mailto:support@omniworth.net" className="text-accent-600 hover:underline">support@omniworth.net</a></li>
                            <li>Contact form: <Link to="/contact" className="text-accent-600 hover:underline">omniworth.net/contact</Link></li>
                        </ul>
                    </section>
                </div>

                {/* Back link */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                    <Link to="/" className="text-accent-600 hover:underline text-sm">
                        &larr; Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
}
